import React, { useMemo, useState } from "react"

import moment, { type Moment } from "moment-timezone"

import { FeatureFlag } from "@common-shared/config/featureFlags.config.ts"
import { PolicyRangeDateFilterType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config.ts"

import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { DatePickerElement } from "@front-shared/elements/datePicker.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper.tsx"

import { POLICIES_CONFIG } from "../../config/policies.config"
import { RuntimeHelper } from "../../helpers/runtime.helper.ts"
import { UtilHelper } from "../../helpers/util.helper.tsx"
import { useHasPermissions } from "../../hooks/hasPermissions.hook.ts"
import { useIsFeatureFlagEnabled } from "../../hooks/isFeatureFlagEnabled.hook.ts"
import { useAppDispatch } from "../../store/appDispatch.hook.ts"
import { useAppSelector } from "../../store/selectors/app.selector"
import { policiesSelector } from "../../store/selectors/policies.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector.ts"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { taskAssignmentsExportProductivity } from "../../store/thunks/taskAssignments.thunks.ts"

import exportIcon from "@front-shared/assets/images/icons/download-down-dark.svg"

export const TaskAssignmentsProductivityExportComponent: React.FunctionComponent<{
    className?: string
}> = props => {
    const dispatch = useAppDispatch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)
    const { policiesGettingFullListProgress } = useAppSelector(policiesSelector)

    const canManageTaskAssignments = useHasPermissions(
        OneVerifyPermission.manageTaskAssignments,
        undefined,
        false,
    )

    const exportFeatureFlagEnabled = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_TASKS_PRODUCTIVITY_EXPORT,
    )

    const isExportLoading = useMemo(() => {
        return RuntimeHelper.isTaskAssignmentsExportProductivityLoading()
    }, [runtimeState.isLoading])

    const [startDate, setStartDate] = useState<Moment>(
        moment()
            .subtract(
                POLICIES_CONFIG.assignmentsProductivityExport
                    .assignmentDateLookbackDays,
                "days",
            )
            .startOf("day"),
    )

    const [endDate, setEndDate] = useState<Moment>(moment().endOf("day"))

    const onExport = () => {
        if (!selectedPracticeRole) {
            return
        }

        if (endDate.isBefore(startDate, "day")) {
            ToastrHelper.error("End date should not be before start date")

            return
        }

        dispatch(
            taskAssignmentsExportProductivity({
                practice: selectedPracticeRole.practice,
                payload: {
                    dateAssigned: {
                        type: PolicyRangeDateFilterType.CUSTOM_RANGE,
                        dateFrom: UtilHelper.dateToMysqlFormat(
                            startDate.toDate(),
                        ),
                        dateTo: UtilHelper.dateToMysqlFormat(endDate.toDate()),
                    },
                },
            }),
        )
    }

    if (!canManageTaskAssignments || !exportFeatureFlagEnabled) {
        return null
    }

    return (
        <div
            className={`
                rounded-[16px] bg-brand-warmLight05 p-[16px]
                ${props.className || ""}
            `}
        >
            <PrimaryText typography="subtitle">
                Workflow Management Productivity
            </PrimaryText>

            <div className="mt-[44px] flex items-end gap-[12px] md:block">
                <DatePickerElement
                    className="w-[240px]! md:w-full!"
                    name="startDate"
                    label="Assignment Start Date"
                    defaultValue={startDate}
                    onChange={value => {
                        if (!value) {
                            return
                        }

                        setStartDate(value)
                    }}
                    maxDate={new Date()}
                    disabled={isExportLoading}
                />

                <DatePickerElement
                    className="w-[240px]! md:w-full! md:mt-[32px]"
                    name="endDate"
                    label="Assignment End Date"
                    defaultValue={endDate}
                    onChange={value => {
                        if (!value) {
                            return
                        }

                        setEndDate(value)
                    }}
                    minDate={startDate?.toDate()}
                    maxDate={new Date()}
                    disabled={isExportLoading}
                />

                <ButtonElement
                    className="md:mt-[16px]"
                    buttonClassName="md:w-full!"
                    label={`Download CSV${
                        isExportLoading
                            ? ` (${policiesGettingFullListProgress}%)`
                            : ""
                    }`}
                    icon={exportIcon}
                    type="default"
                    htmlType="button"
                    onClick={onExport}
                    isLoading={isExportLoading}
                    disabled={!startDate || !endDate}
                />
            </div>
        </div>
    )
}
